import React, { useState, useEffect } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./SchedulerPage.css";
import CustomToolbar from "../components/CustomToolbar";
import DateDetailsWidget from "../components/DateDetailsWidget";
import { useLocation } from "react-router-dom";
import OuraNav from "../components/OuraNav";
import { useRef } from "react";
import fetchBase from "../utils/fetchBase";

const locales = { "en-US": enUS };

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});



const CustomEvent = ({ event, onSelectEvent, onDeleted }) => {
    const [hovered, setHovered] = useState(false);

    const handleDelete = async (e) => {
        e.stopPropagation();
        const confirmed = window.confirm("Are you sure you want to delete this schedule?");
        if (!confirmed) return;

        const auth = "Bearer " + localStorage.getItem("jwt_token");

        try {
            const res = await fetch(`${fetchBase}/api/schedule/delete/${event.resource.scheduleId}`, {
                method: "DELETE",
                headers: { Authorization: auth },
            });

            if (res.ok) {
                onDeleted?.();
            } else {
                alert("Failed to delete schedule.");
            }
        } catch (err) {
            console.error("Delete failed", err);
            alert("Error deleting schedule.");
        }
    };

    const clientLabel = event.clientFirstName
        ? `, ${event.clientFirstName} ${event.clientLastName?.charAt(0)}.`
        : "";

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={(e) => {
                e.stopPropagation();
                event.resource.onSelect(event);
            }}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
        >
            <div>
                <span>{event.title}</span>
                <span style={{ color: "gray", marginLeft: "4px" }}>{clientLabel}</span>
            </div>
            {hovered && (
                <button
                    onClick={handleDelete}
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "red",
                        fontWeight: "bold",
                        cursor: "pointer",
                        marginLeft: "10px",
                    }}
                    title="Delete schedule"
                >
                    🗑
                </button>
            )}
        </div>
    );
};

export default function SchedulerPage() {
    const location = useLocation();
    const initialDate = location.state?.selectedDate
        ? new Date(location.state.selectedDate)
        : null;

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [events, setEvents] = useState([]);
    const [popupOpen, setPopupOpen] = useState(!!initialDate);
    const [selectedDate, setSelectedDate] = useState(initialDate);
    const [calendarView, setCalendarView] = useState("month");
    // Create a ~state variable~ called CalendarLabel that stores the visible month and year (like July 2025)
    const [calendarLabel, setCalendarLabel] = useState("");
    const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());
    const [copyingSchedule, setCopyingSchedule] = useState(null);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [editedSchedule, setEditedSchedule] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [editClients, setEditClients] = useState([]);
    const [editRates, setEditRates] = useState([]);
    const [editTimeBuffer, setEditTimeBuffer] = useState("");




    const fetchSchedules = async () => {
            const auth = "Bearer " + localStorage.getItem("jwt_token");
            console.log("fetchSchedules called, token:", localStorage.getItem("jwt_token")?.slice(0,20));
            if (!auth) return;

        try {
            const res = await fetch(`${fetchBase}/api/schedule/by-admin`, {
                headers: { Authorization: auth },
            });

            if (res.ok) {
                const data = await res.json();
                console.log("RAW schedule data:", JSON.stringify(data[0]));
                const formatted = data.map(event => ({
                    title: event.employeeFirstName,
                    clientFirstName: event.clientFirstName,
                    clientLastName: event.clientLastName,
                    start: new Date(event.start),
                    end: new Date(event.end),
                        resource: {
                            color: event.clientColor,
                            scheduleId: event.id,
                            serviceCode: event.serviceCode,
                            shiftHours: event.shiftHours,
                            employeeId: event.employeeId,
                            clientId: event.clientId,
                            billingRate: event.billingRate,
                            unavailable: event.unavailable,
                            onSelect: (ev) => {
                                setSelectedSchedule(ev);
                                setEditedSchedule(ev);
                                setIsDirty(false);
                                setEditingField(null);
                            },
                        },

                }));
                setEvents(formatted);
            }
        } catch (err) {
            console.error("Failed to fetch schedules", err);
        }
    };

    useEffect(() => {
        fetchSchedules();

        // Set the initial calendar label to the current month and year
        const today = new Date();
        setCalendarLabel(localizer.format(today, "MMMM yyyy"));
    }, []);

    const handleDateClick = ({ start }) => {
            if (copyingSchedule) {
                setCopyingSchedule({ ...copyingSchedule, pasteDate: start });
                return;
            }
            setSelectedDate(start);
            setPopupOpen(true);
    };
    // Allows ORA-Nav to navigate the calendar using real button clicks
    // Handle calendar navigation: Today, Prev, Next
    const calendarRef = useRef(null);

// Handles navigation from OuraNav using ref-based control
    const handleNavigate = (action) => {
        const calendarApi = calendarRef.current;
        if (!calendarApi) return;

        const currentDate = currentCalendarDate || new Date();
        let newDate = new Date(currentDate);

        switch (action) {
            case "TODAY":
                newDate = new Date();
                break;
            case "NEXT":
                if (calendarView === "month") newDate.setMonth(newDate.getMonth() + 1);
                if (calendarView === "week") newDate.setDate(newDate.getDate() + 7);
                if (calendarView === "day") newDate.setDate(newDate.getDate() + 1);
                break;
            case "PREV":
                if (calendarView === "month") newDate.setMonth(newDate.getMonth() - 1);
                if (calendarView === "week") newDate.setDate(newDate.getDate() - 7);
                if (calendarView === "day") newDate.setDate(newDate.getDate() - 1);
                break;
            default:
                return;
        }

        // Update local state that feeds the <Calendar date={...} />
        setCurrentCalendarDate(newDate);
        setCalendarLabel(localizer.format(newDate, "MMMM yyyy"));
    };

    return (
        <>
            <OuraNav
                links={[
                    { label: "Dashboard", href: "/admin-dashboard" },
                    { label: "Employees", href: "/employees" },
                    { label: "Clients", href: "/admin/clients" },
                    { label: "Activity", href: "/activity" },
                    { label: "Payroll", href: "/admin/payroll" }
                ]}
                // Connects ORA-Nav navigation buttons to calendar control logic
                calendarControls={{
                    label: calendarLabel,                // the "July 2025" string
                    onNavigate: handleNavigate,
                    onView: setCalendarView,            // changes between month/week/day
                    view: calendarView,                 // current view (month/week/day)
                    views: ["month", "week", "day"]     // which views to display as buttons
                }}
            />
            <div className="scheduler-wrapper">
                <div className="scheduler-body">
                    {isSidebarOpen && (
                        <div className="sidebar-menu sidebar-slide-enter-active">
                            <h2>Schedule</h2>
                            <ul>
                                <li>Employee</li>
                                <li>Event</li>
                            </ul>
                        </div>
                    )}

                    {copyingSchedule && !copyingSchedule.pasteDate && (
                        <div style={{
                            position: "fixed",
                            top: "66px",
                            left: "20px",
                            background: "rgba(28,28,30,0.92)",
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "600",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            zIndex: 9998,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            top: "45px",
                        }}>
                            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#34c759", display: "inline-block" }} />
                            Copy Mode
                            <button
                                onClick={() => setCopyingSchedule(null)}
                                style={{
                                    background: "none", border: "none",
                                    color: "#ff3b30", fontWeight: "700",
                                    cursor: "pointer", fontSize: "12px",
                                    marginLeft: "4px"
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    <main className="calendar-view">
                        <Calendar
                            ref={calendarRef}
                            localizer={localizer}
                            events={events}
                            startAccessor="start"
                            endAccessor="end"
                            date={currentCalendarDate}
                            views={["month", "week", "day"]}
                            view={calendarView}
                            onView={setCalendarView}
                            showAllEvents={true}
                            // Update the calendarLabel state when user navigates months (e.g. with ← → Today buttons)
                            onNavigate={(date, view) => {
                                setCalendarLabel(localizer.format(date, "MMMM yyyy")); // example: "July 2025"
                            }}
                            selectable
                            onSelectSlot={handleDateClick}
                            eventPropGetter={(event) => ({
                                style: event.resource?.unavailable ? {
                                    backgroundColor: "#e6f1fb",
                                    borderLeft: "6px solid #185fa5",
                                    color: "#0c447c",
                                    paddingLeft: "10px",
                                    borderRadius: "4px",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                } : {
                                    backgroundColor: "#fff",
                                    borderLeft: `6px solid ${event.resource?.color || "#ccc"}`,
                                    color: "#000",
                                    paddingLeft: "10px",
                                    borderRadius: "4px",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                },
                            })}
                            components={{
                                toolbar: () => <></>, // Override default toolbar with nothing
                                event: (props) => <CustomEvent {...props} onDeleted={fetchSchedules} />,
                            }}
                        />
                    </main>

                    {popupOpen && (
                            <DateDetailsWidget
                                selectedDate={selectedDate}
                                onClose={() => setPopupOpen(false)}
                                refreshEvents={fetchSchedules}
                            />
                        )}

                        {/* Small schedule detail + copy modal */}
                            {selectedSchedule && !copyingSchedule?.pasteDate && (
                                <div
                                    style={{
                                        position: "fixed", top: 0, left: 0,
                                        width: "100vw", height: "100vh",
                                        background: "rgba(0,0,0,0.4)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        zIndex: 9999
                                    }}
                                    onClick={() => {
                                        if (isDirty) return;
                                        setSelectedSchedule(null);
                                    }}
                                >
                                    {saveStatus && (
                                        <div style={{
                                            position: "fixed",
                                            top: "45px",
                                            left: "20px",
                                            background: "rgba(28,28,30,0.92)",
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            padding: "6px 12px",
                                            borderRadius: "8px",
                                            zIndex: 9998,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}>
                                            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: saveStatus === "saving" ? "#ff9500" : "#34c759", display: "inline-block" }} />
                                            {saveStatus === "saving" ? "Saving..." : "Saved"}
                                        </div>
                                    )}
                                <div
                                    style={{
                                        background: "rgba(28,28,30,0.97)",
                                        borderRadius: "14px",
                                        padding: "16px 18px",
                                        width: "260px",
                                        color: "white",
                                        fontSize: "13px",
                                        position: "relative",
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)"
                                    }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    {/* Delete / Save button top left */}
                                    {isDirty ? (
                                        <button
                                            onClick={async () => {
                                                setSaveStatus("saving");
                                                const auth = "Bearer " + localStorage.getItem("jwt_token");
                                                const startDate = new Date(editedSchedule.start);
                                                let endDate = new Date(editedSchedule.end);
                                                const pad = (n) => n.toString().padStart(2, "0");
                                                const dateStr = `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}`;
                                                const startTimeStr = `${pad(startDate.getHours())}:${pad(startDate.getMinutes())}:00`;
                                                const endTimeStr = `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}:00`;
                                                let shiftHours = (endDate - startDate) / (1000 * 60 * 60);
                                                if (shiftHours <= 0) shiftHours += 24;

                                                try {
                                                    const res = await fetch(`${fetchBase}/api/schedule/${editedSchedule.resource.scheduleId}`, {
                                                        method: "PUT",
                                                        headers: {
                                                            "Content-Type": "application/json",
                                                            Authorization: auth
                                                        },
                                                        body: JSON.stringify({
                                                            employeeId: editedSchedule.resource.employeeId,
                                                            clientId: editedSchedule.resource.clientId,
                                                            clientColor: editedSchedule.resource.color,
                                                            date: dateStr,
                                                            startTime: startTimeStr,
                                                            endTime: endTimeStr,
                                                            shiftHours: shiftHours,
                                                            serviceCode: editedSchedule.resource.serviceCode,
                                                            billingRate: editedSchedule.resource.billingRate
                                                        })
                                                    });
                                                    if (res.ok) {
                                                        setSaveStatus("saved");
                                                        setTimeout(() => {
                                                            setSaveStatus(null);
                                                            setSelectedSchedule(null);
                                                            setEditedSchedule(null);
                                                            setIsDirty(false);
                                                            fetchSchedules();
                                                        }, 1000);
                                                    } else {
                                                        setSaveStatus(null);
                                                        alert("Failed to save changes.");
                                                    }
                                                } catch (err) {
                                                    setSaveStatus(null);
                                                    alert("Error saving changes.");
                                                }
                                            }}
                                            style={{
                                                position: "absolute", top: "12px", left: "12px",
                                                background: "#34c759", border: "none",
                                                color: "white", fontWeight: "600",
                                                fontSize: "11px", padding: "4px 10px",
                                                borderRadius: "6px", cursor: "pointer"
                                            }}
                                        >
                                            Save
                                        </button>
                                    ) : (
                                        <button
                                            onClick={async () => {
                                                const confirmed = window.confirm("Are you sure you want to delete this schedule?");
                                                if (!confirmed) return;
                                                const auth = "Bearer " + localStorage.getItem("jwt_token");
                                                const res = await fetch(`${fetchBase}/api/schedule/delete/${selectedSchedule.resource.scheduleId}`, {
                                                    method: "DELETE",
                                                    headers: { Authorization: auth },
                                                });
                                                if (res.ok) {
                                                    setSelectedSchedule(null);
                                                    fetchSchedules();
                                                } else {
                                                    alert("Failed to delete schedule.");
                                                }
                                            }}
                                            style={{
                                                position: "absolute", top: "12px", left: "12px",
                                                background: "#ff3b30", border: "none",
                                                color: "white", fontWeight: "600",
                                                fontSize: "11px", padding: "4px 10px",
                                                borderRadius: "6px", cursor: "pointer"
                                            }}
                                        >
                                            Delete
                                        </button>
                                    )}

                                    {/* Copy / Discard button top right */}
                                        {isDirty ? (
                                            <button
                                                onClick={() => {
                                                    setEditedSchedule(selectedSchedule);
                                                    setIsDirty(false);
                                                    setEditingField(null);
                                                }}
                                                style={{
                                                    position: "absolute", top: "12px", right: "12px",
                                                    background: "white", border: "none",
                                                    color: "black", fontWeight: "600",
                                                    fontSize: "11px", padding: "4px 10px",
                                                    borderRadius: "6px", cursor: "pointer"
                                                }}
                                            >
                                                Discard
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    setCopyingSchedule(selectedSchedule);
                                                    setSelectedSchedule(null);
                                                }}
                                                style={{
                                                    position: "absolute", top: "12px", right: "12px",
                                                    background: "white", border: "none",
                                                    color: "black", fontWeight: "600",
                                                    fontSize: "11px", padding: "4px 10px",
                                                    borderRadius: "6px", cursor: "pointer"
                                                }}
                                            >
                                                Copy
                                            </button>
                                        )}

                                    <p style={{ fontWeight: "600", fontSize: "13px", marginBottom: "12px", marginRight: "60px", marginLeft: "60px", textAlign: "center", marginTop: "28px" }}>
                                        {new Date(selectedSchedule.start).toLocaleDateString("en-US", {
                                            weekday: "short", month: "short", day: "numeric"
                                        })}
                                    </p>

                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px" }}>
                                        <span style={{ color: "#aaa" }}>Employee</span>
                                        <span style={{ fontWeight: "500", textAlign: "right", maxWidth: "150px" }}>{selectedSchedule.title}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px", alignItems: "center" }}>
                                        <span style={{ color: "#aaa" }}>Client</span>
                                        {editingField === "client" ? (
                                            <select
                                                autoFocus
                                                value={editedSchedule.resource.clientId}
                                                onChange={(e) => {
                                                    const newClientId = parseInt(e.target.value);
                                                    const newClient = editClients.find(c => c.id === newClientId);
                                                    setEditedSchedule(prev => ({
                                                        ...prev,
                                                        clientFirstName: newClient?.firstName,
                                                        clientLastName: newClient?.lastName,
                                                        resource: { ...prev.resource, clientId: newClientId, color: newClient?.color }
                                                    }));
                                                    setIsDirty(true);
                                                }}
                                                onBlur={() => setEditingField(null)}
                                                style={{ maxWidth: "150px", fontSize: "12px", background: "#1d1d1f", color: "white", border: "1px solid #555", borderRadius: "4px" }}
                                            >
                                                {editClients.map(c => (
                                                    <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span
                                                onClick={async () => {
                                                    if (editClients.length === 0) {
                                                        const auth = "Bearer " + localStorage.getItem("jwt_token");
                                                        const res = await fetch(`${fetchBase}/api/client/by-admin`, { headers: { Authorization: auth } });
                                                        const data = await res.json();
                                                        if (Array.isArray(data)) setEditClients(data);
                                                    }
                                                    setEditingField("client");
                                                }}
                                                style={{ fontWeight: "500", textAlign: "right", maxWidth: "150px", cursor: "pointer" }}
                                            >
                                                {editedSchedule.clientFirstName || ""} {editedSchedule.clientLastName || ""}
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px", alignItems: "center" }}>
                                        <span style={{ color: "#aaa" }}>Start</span>
                                        {editingField === "start" ? (
                                            <input
                                                autoFocus
                                                type="time"
                                                value={editTimeBuffer}
                                                onChange={(e) => setEditTimeBuffer(e.target.value)}
                                                onBlur={() => {
                                                    if (editTimeBuffer) {
                                                        const [h, m] = editTimeBuffer.split(":").map(Number);
                                                        if (!isNaN(h) && !isNaN(m)) {
                                                            const newStart = new Date(editedSchedule.start);
                                                            newStart.setHours(h, m);
                                                            setEditedSchedule(prev => ({ ...prev, start: newStart }));
                                                            setIsDirty(true);
                                                        }
                                                    }
                                                    setEditingField(null);
                                                }}
                                                style={{ maxWidth: "150px", fontSize: "12px", background: "#1d1d1f", color: "white", border: "1px solid #555", borderRadius: "4px" }}
                                            />
                                        ) : (
                                            <span
                                                onClick={() => {
                                                    const d = new Date(editedSchedule.start);
                                                    setEditTimeBuffer(`${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`);
                                                    setEditingField("start");
                                                }}
                                                style={{ fontWeight: "500", textAlign: "right", maxWidth: "150px", cursor: "pointer" }}
                                            >
                                                {new Date(editedSchedule.start).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px", alignItems: "center" }}>
                                        <span style={{ color: "#aaa" }}>End</span>
                                        {editingField === "end" ? (
                                            <input
                                                autoFocus
                                                type="time"
                                                value={editTimeBuffer}
                                                onChange={(e) => setEditTimeBuffer(e.target.value)}
                                                onBlur={() => {
                                                    if (editTimeBuffer) {
                                                        const [h, m] = editTimeBuffer.split(":").map(Number);
                                                        if (!isNaN(h) && !isNaN(m)) {
                                                            const newEnd = new Date(editedSchedule.end);
                                                            newEnd.setHours(h, m);
                                                            setEditedSchedule(prev => ({ ...prev, end: newEnd }));
                                                            setIsDirty(true);
                                                        }
                                                    }
                                                    setEditingField(null);
                                                }}
                                                style={{ maxWidth: "150px", fontSize: "12px", background: "#1d1d1f", color: "white", border: "1px solid #555", borderRadius: "4px" }}
                                            />
                                        ) : (
                                            <span
                                                onClick={() => {
                                                    const d = new Date(editedSchedule.end);
                                                    setEditTimeBuffer(`${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`);
                                                    setEditingField("end");
                                                }}
                                                style={{ fontWeight: "500", textAlign: "right", maxWidth: "150px", cursor: "pointer" }}
                                            >
                                                {new Date(editedSchedule.end).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px" }}>
                                        <span style={{ color: "#aaa" }}>Duration</span>
                                        <span style={{ fontWeight: "500", textAlign: "right", maxWidth: "150px" }}>
                                            {(() => {
                                                const start = new Date(editedSchedule.start);
                                                let end = new Date(editedSchedule.end);
                                                if (end <= start) {
                                                    end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
                                                }
                                                return ((end - start) / (1000 * 60 * 60)).toFixed(2);
                                            })()} hrs
                                        </span>
                                    </div>


                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px", alignItems: "center" }}>
                                            <span style={{ color: "#aaa" }}>Service Code</span>
                                            <select
                                                value={editedSchedule.resource.serviceCode || ""}
                                                onChange={(e) => {
                                                    setEditedSchedule(prev => ({
                                                        ...prev,
                                                        resource: { ...prev.resource, serviceCode: e.target.value }
                                                    }));
                                                    setIsDirty(true);
                                                }}
                                                style={{ maxWidth: "150px", fontSize: "12px", background: "#1d1d1f", color: "white", border: "1px solid #555", borderRadius: "4px" }}
                                            >
                                                <option value="">Select Code</option>
                                                <option value="HHA *G0156*">HHA *G0156*</option>
                                                <option value="Respite *S5150*">Respite *S5150*</option>
                                                <option value="RN VISIT*G0162*">RN VISIT*G0162*</option>
                                            </select>
                                        </div>

                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px", alignItems: "center" }}>
                                        <span style={{ color: "#aaa" }}>Rate</span>
                                        {editingField === "rate" ? (
                                            <select
                                                autoFocus
                                                value={editedSchedule.resource.billingRate ?? ""}
                                                onChange={(e) => {
                                                    setEditedSchedule(prev => ({
                                                        ...prev,
                                                        resource: { ...prev.resource, billingRate: parseFloat(e.target.value) }
                                                    }));
                                                    setIsDirty(true);
                                                }}
                                                onBlur={() => setEditingField(null)}
                                                style={{ maxWidth: "150px", fontSize: "12px", background: "#1d1d1f", color: "white", border: "1px solid #555", borderRadius: "4px" }}
                                            >
                                                {editRates.length === 0 && editedSchedule.resource.billingRate != null && (
                                                    <option value={editedSchedule.resource.billingRate}>{editedSchedule.resource.billingRate}</option>
                                                )}
                                                {editRates.map((rate, idx) => (
                                                    <option key={idx} value={rate}>{rate}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span
                                                onClick={async () => {
                                                    if (editRates.length === 0) {
                                                        const auth = "Bearer " + localStorage.getItem("jwt_token");
                                                        const res = await fetch(`${fetchBase}/api/schedule/recent-rates`, { headers: { Authorization: auth } });
                                                        const data = await res.json();
                                                        if (Array.isArray(data)) setEditRates(data);
                                                    }
                                                    setEditingField("rate");
                                                }}
                                                style={{ fontWeight: "500", textAlign: "right", maxWidth: "150px", cursor: "pointer" }}
                                            >
                                                {editedSchedule.resource?.billingRate != null ? editedSchedule.resource.billingRate : "—"}
                                            </span>
                                        )}
                                    </div>

                                </div>
                            </div>
                        )}

                        {/* Copy confirmation modal — shown after admin picks a paste date */}
                        {copyingSchedule?.pasteDate && (
                            <div
                                style={{
                                    position: "fixed", top: 0, left: 0,
                                    width: "100vw", height: "100vh",
                                    background: "rgba(0,0,0,0.4)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    zIndex: 9999
                                }}
                                onClick={() => setCopyingSchedule(null)}
                            >
                                <div
                                    style={{
                                        background: "rgba(28,28,30,0.97)",
                                        borderRadius: "14px",
                                        padding: "16px 18px",
                                        width: "260px",
                                        color: "white",
                                        fontSize: "13px",
                                        position: "relative",
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)"
                                    }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <button
                                        onClick={() => setCopyingSchedule(null)}
                                        style={{
                                            position: "absolute", top: "12px", right: "12px",
                                            background: "#ff3b30", border: "none",
                                            color: "white", fontWeight: "600",
                                            fontSize: "11px", padding: "4px 10px",
                                            borderRadius: "6px", cursor: "pointer"
                                        }}
                                    >
                                        End Copy
                                    </button>

                                    <p style={{ fontWeight: "600", fontSize: "13px", marginBottom: "12px", marginRight: "80px" }}>
                                        {copyingSchedule.pasteDate.toLocaleDateString("en-US", {
                                            weekday: "short", month: "short", day: "numeric"
                                        })}
                                    </p>

                                    {[
                                        ["Employee", copyingSchedule.title],
                                        ["Client", `${copyingSchedule.clientFirstName || ""} ${copyingSchedule.clientLastName || ""}`],
                                        ["Start", new Date(copyingSchedule.start).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })],
                                        ["End", new Date(copyingSchedule.end).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })],
                                        ["Duration", `${copyingSchedule.resource?.shiftHours ?? "—"} hrs`],
                                        ["Service Code", copyingSchedule.resource?.serviceCode || "—"],
                                    ].map(([label, value]) => (
                                        <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px" }}>
                                            <span style={{ color: "#aaa" }}>{label}</span>
                                            <span style={{ fontWeight: "500", textAlign: "right", maxWidth: "150px" }}>{value}</span>
                                        </div>
                                    ))}

                                    <button
                                        onClick={async () => {
                                            const token = localStorage.getItem("jwt_token");
                                            const rawPaste = copyingSchedule.pasteDate;
                                            const pasteDate = `${rawPaste.getFullYear()}-${String(rawPaste.getMonth()+1).padStart(2,"0")}-${String(rawPaste.getDate()).padStart(2,"0")}`;
                                            const clientId = copyingSchedule.resource?.clientId;
                                            const shiftHours = copyingSchedule.resource?.shiftHours;

                                            const periodRes = await fetch(`${fetchBase}/api/pay-period/active`, {
                                                headers: { Authorization: `Bearer ${token}` }
                                            });
                                            let period = null;
                                            if (periodRes.ok) {
                                                const text = await periodRes.text();
                                                if (text) period = JSON.parse(text);
                                            }

                                            if (!period) {
                                                alert("No active pay period found.");
                                                return;
                                            }

                                            const [startY, startM, startD] = period.startDate.split("-").map(Number);
                                            const [endY, endM, endD] = period.endDate.split("-").map(Number);
                                            const periodStart = new Date(startY, startM - 1, startD);
                                            const periodEnd = new Date(endY, endM - 1, endD);
                                            const targetDate = new Date(rawPaste);
                                            targetDate.setHours(0, 0, 0, 0);
                                            periodStart.setHours(0, 0, 0, 0);
                                            periodEnd.setHours(0, 0, 0, 0);

                                            if (targetDate < periodStart || targetDate > periodEnd) {
                                                const confirmed = window.confirm(`${targetDate.toLocaleDateString()} is outside the active pay period (${period.startDate} to ${period.endDate}). Copy this client's schedule pattern into the next pay period instead?`);
                                                if (!confirmed) return;
                                                    const forwardRes = await fetch(`${fetchBase}/api/schedule/copy-forward`, {
                                                        method: "POST",
                                                        headers: {
                                                            "Content-Type": "application/json",
                                                            Authorization: `Bearer ${token}`
                                                        },
                                                        body: JSON.stringify({
                                                            employeeId: copyingSchedule.resource?.employeeId,
                                                            clientId: clientId,
                                                            anchorDate: pasteDate
                                                        })
                                                    });
                                                if (!forwardRes.ok) {
                                                    const errText = await forwardRes.text();
                                                    alert(errText || "Failed to copy schedule to next pay period.");
                                                    return;
                                                }
                                                setCopyingSchedule(null);
                                                fetchSchedules();
                                                return;
                                            }

                                            const remainingRes = await fetch(`${fetchBase}/api/schedule/remaining-hours/${clientId}?date=${pasteDate}`, {
                                                headers: { Authorization: `Bearer ${token}` }
                                            });
                                            if (remainingRes.ok) {
                                                const remainingData = await remainingRes.json();
                                                if (remainingData.hasActivePeriod && remainingData.remainingHours !== undefined && remainingData.remainingHours < shiftHours) {
                                                    alert("⚠️ No hours remaining for this client this week.");
                                                    return;
                                                }
                                            }

                                            const startDate = new Date(copyingSchedule.start);
                                            const endDate = new Date(copyingSchedule.end);
                                            const startTime = `${startDate.getHours().toString().padStart(2,"0")}:${startDate.getMinutes().toString().padStart(2,"0")}:00`;
                                            const endTime = `${endDate.getHours().toString().padStart(2,"0")}:${endDate.getMinutes().toString().padStart(2,"0")}:00`;

                                            await fetch(`${fetchBase}/api/schedule/add`, {
                                                method: "POST",
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    Authorization: `Bearer ${token}`
                                                },
                                                body: JSON.stringify({
                                                    employeeId: copyingSchedule.resource?.employeeId,
                                                    clientId: clientId,
                                                    clientColor: copyingSchedule.resource?.color,
                                                    date: pasteDate,
                                                    startTime,
                                                    endTime,
                                                    shiftHours,
                                                    serviceCode: copyingSchedule.resource?.serviceCode,
                                                    billingRate: copyingSchedule.resource?.billingRate,
                                                })
                                            });

                                            setCopyingSchedule(prev => ({ ...prev, pasteDate: undefined }));
                                            fetchSchedules();
                                        }}
                                        style={{
                                            width: "100%", marginTop: "12px",
                                            padding: "9px", borderRadius: "8px",
                                            background: "white", border: "none",
                                            color: "black", fontWeight: "600",
                                            fontSize: "13px", cursor: "pointer"
                                        }}
                                    >
                                        Schedule
                                    </button>
                                </div>
                            </div>
                        )}
                </div>
            </div>
            </>
            );
            }
